import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gestoriaAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Scale, Clock, Loader2, CheckCircle2, Archive, ArrowLeft,
  Plane, Paperclip, Calendar, AlertCircle, Send, MessageSquare,
  X, User, Shield,
} from 'lucide-react';
import { formatApiError } from '../../utils/apiError';

const ESTADOS = [
  { key: 'pendiente', label: 'Recibido', icon: Clock, desc: 'Tu solicitud fue recibida y está en cola para ser revisada.' },
  { key: 'en_proceso', label: 'En proceso', icon: Loader2, desc: 'Uno de nuestros profesionales está trabajando en tu solicitud.' },
  { key: 'resuelto', label: 'Resuelto', icon: CheckCircle2, desc: '¡Tu solicitud fue resuelta!' },
];
const MAX_FILES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const formatDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('es-AR', { dateStyle: 'long', timeStyle: 'short' }); }
  catch { return iso; }
};
const formatShort = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

// ============================================
// Sección de conversación
// ============================================
const Conversacion = ({ token, disabled }) => {
  const [comentarios, setComentarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await gestoriaAPI.listarComentariosPublico(token);
      setComentarios(res.data?.items || []);
    } catch { setComentarios([]); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    if (files.length + incoming.length > MAX_FILES) { toast.error(`Máximo ${MAX_FILES} archivos`); return; }
    const tooBig = incoming.find((f) => f.size > MAX_FILE_SIZE);
    if (tooBig) { toast.error(`"${tooBig.name}" supera los 10MB`); return; }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    const bad = incoming.find((f) => !allowed.includes(f.type));
    if (bad) { toast.error(`Tipo no permitido: "${bad.name}"`); return; }
    setFiles((p) => [...p, ...incoming]);
    e.target.value = '';
  };
  const removeFile = (idx) => setFiles((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!texto.trim() && files.length === 0) { toast.error('Escribí un mensaje o adjuntá un archivo'); return; }
    setSending(true);
    try {
      await gestoriaAPI.crearComentarioPublico(token, texto.trim() || '(Archivo adjunto)', files);
      setTexto('');
      setFiles([]);
      toast.success('Mensaje enviado');
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'No pudimos enviar el mensaje'));
    } finally { setSending(false); }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-5">
          <MessageSquare className="h-5 w-5 text-violet-600" />
          <h2 className="text-lg font-semibold text-slate-900">Conversación con el equipo</h2>
          {comentarios.length > 0 && <Badge variant="outline" className="ml-auto">{comentarios.length} mensaje{comentarios.length === 1 ? '' : 's'}</Badge>}
        </div>

        {loading ? (
          <div className="py-6 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : comentarios.length === 0 ? (
          <p className="text-sm text-slate-500 italic mb-4 text-center py-4">
            Todavía no hay mensajes. Podés escribirnos lo que necesites — también podés adjuntar archivos.
          </p>
        ) : (
          <div className="space-y-4 mb-5 max-h-[500px] overflow-y-auto pr-1">
            {comentarios.map((c) => {
              const esAdmin = c.autor === 'admin';
              return (
                <div key={c.id} className={`flex gap-3 ${esAdmin ? '' : 'flex-row-reverse'}`} data-testid={`seguimiento-comentario-${c.id}`}>
                  <div className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${esAdmin ? 'bg-violet-600' : 'bg-slate-300'}`}>
                    {esAdmin ? <Shield className="h-4 w-4 text-white" /> : <User className="h-4 w-4 text-slate-700" />}
                  </div>
                  <div className={`flex-1 max-w-[85%] ${esAdmin ? '' : 'text-right'}`}>
                    <div className={`inline-block text-left rounded-2xl px-4 py-3 ${esAdmin ? 'bg-violet-50 border border-violet-100' : 'bg-slate-100'}`}>
                      <p className={`text-xs font-medium mb-1 ${esAdmin ? 'text-violet-700' : 'text-slate-700'}`}>
                        {esAdmin ? 'C&M Servicios Aeronáuticos' : (c.autor_nombre || 'Vos')}
                      </p>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{c.texto}</p>
                      {c.adjuntos?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {c.adjuntos.map((a) => (
                            <a key={a.id} href={gestoriaAPI.adjuntoPublicoUrl(token, a.id)} target="_blank" rel="noopener noreferrer"
                               className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border ${esAdmin ? 'bg-white border-violet-200 hover:bg-violet-100' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                              <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-violet-500" />
                              <span className="truncate flex-1 text-slate-700">{a.filename}</span>
                              <span className="text-slate-400">{(a.size / 1024).toFixed(0)}KB</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1 px-1">{formatShort(c.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {disabled ? (
          <p className="text-sm text-center text-slate-500 bg-slate-50 border rounded-md p-3">
            Esta solicitud fue archivada. Si necesitás contactarnos, escribinos a <a href="mailto:administracion@cmpublicaciones.com" className="text-violet-600 hover:underline">administracion@cmpublicaciones.com</a>.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-3 border-t pt-5">
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escribí tu mensaje..."
              rows={3}
              className="resize-y"
              data-testid="seguimiento-comentario-texto"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={files.length >= MAX_FILES} data-testid="seguimiento-attach-btn">
                <Paperclip className="mr-2 h-4 w-4" /> Adjuntar
              </Button>
              <span className="text-xs text-slate-500">{files.length}/{MAX_FILES} • PNG, JPG, PDF — 10MB c/u</span>
              <input ref={fileInputRef} type="file" hidden multiple accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf" onChange={handleFiles} />
              <Button type="submit" disabled={sending} className="ml-auto bg-violet-600 hover:bg-violet-500 text-white" data-testid="seguimiento-comentario-submit">
                {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</> : <><Send className="mr-2 h-4 w-4" /> Enviar</>}
              </Button>
            </div>
            {files.length > 0 && (
              <div className="space-y-1.5">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 border rounded-md px-3 py-1.5 text-sm">
                    <span className="truncate text-slate-700" title={f.name}>{f.name}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-slate-500">{(f.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </form>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================
// PÁGINA
// ============================================
const GestoriaSeguimientoPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await gestoriaAPI.getSeguimiento(token);
        if (active) setData(res.data);
      } catch (e) {
        if (active) setError(e.response?.status === 404 ? 'Seguimiento no encontrado' : 'Error al cargar');
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-10 text-center">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Seguimiento no disponible</h2>
            <p className="text-slate-600 mb-5">El link de seguimiento no es válido o expiró.</p>
            <Button asChild className="bg-violet-600 hover:bg-violet-500"><Link to="/gestoria">Volver a Gestoría</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const esArchivado = data.estado === 'archivado';
  const currentIdx = ESTADOS.findIndex((s) => s.key === data.estado);
  const effectiveIdx = esArchivado ? -1 : currentIdx;

  return (
    <div className="min-h-screen bg-white" data-testid="gestoria-seguimiento">
      <section className="bg-gradient-to-br from-violet-700 via-purple-700 to-violet-900 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4">
          <Button asChild variant="ghost" className="text-white hover:bg-white/10 mb-4">
            <Link to="/gestoria"><ArrowLeft className="mr-1 h-4 w-4" /> Volver</Link>
          </Button>
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-white/10 backdrop-blur p-3 rounded-xl border border-white/20"><Scale className="h-7 w-7 text-white" /></div>
            <div>
              <p className="text-violet-200 text-xs uppercase tracking-wider">Seguimiento de solicitud</p>
              <h1 className="text-2xl md:text-3xl font-bold text-white">{data.tipo_solicitud === 'tramite' ? 'Tu trámite' : 'Tu consulta'}</h1>
            </div>
          </div>
          <p className="text-violet-100">Hola <strong className="text-white">{(data.nombre_completo || '').split(' ')[0]}</strong>, así va tu solicitud:</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        {/* Estado actual */}
        <Card className={`border-2 ${esArchivado ? 'border-slate-300 bg-slate-50' : 'border-violet-200 bg-gradient-to-br from-violet-50 to-white'}`}>
          <CardContent className="p-6 text-center">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Estado actual</p>
            <div className="flex items-center justify-center gap-2 mb-2">
              {esArchivado ? <Archive className="h-7 w-7 text-slate-500" /> : <Badge className="bg-violet-600 text-white text-base px-4 py-1">{data.estado_label}</Badge>}
              {esArchivado && <span className="text-xl font-bold text-slate-600">Archivado</span>}
            </div>
            <p className="text-sm text-slate-600">Última actualización: {formatDate(data.updated_at)}</p>
          </CardContent>
        </Card>

        {/* Timeline */}
        {!esArchivado && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-5">Progreso</h2>
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />
              <div className="space-y-6">
                {ESTADOS.map((step, idx) => {
                  const done = idx < effectiveIdx;
                  const current = idx === effectiveIdx;
                  const StepIcon = step.icon;
                  return (
                    <div key={step.key} className="relative flex gap-4">
                      <div className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${done ? 'bg-violet-600 border-violet-600 text-white' : ''} ${current ? 'bg-violet-100 border-violet-600 text-violet-700 ring-4 ring-violet-100' : ''} ${!done && !current ? 'bg-white border-slate-300 text-slate-400' : ''}`}>
                        {done ? <CheckCircle2 className="h-5 w-5" /> : <StepIcon className={`h-5 w-5 ${current && step.key === 'en_proceso' ? 'animate-spin' : ''}`} />}
                      </div>
                      <div className="flex-1 pb-2">
                        <p className={`font-semibold ${current ? 'text-violet-700' : done ? 'text-slate-900' : 'text-slate-400'}`}>
                          {step.label}
                          {current && <span className="ml-2 text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">Estado actual</span>}
                        </p>
                        <p className={`text-sm ${current || done ? 'text-slate-600' : 'text-slate-400'}`}>{step.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Conversación */}
        <Conversacion token={token} disabled={esArchivado} />

        {/* Detalle */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Detalle</h2>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><dt className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tipo</dt><dd className="text-slate-800 font-medium">{data.tipo_solicitud === 'tramite' ? 'Trámite' : 'Asesoramiento'}</dd></div>
              <div><dt className="text-xs text-slate-500 uppercase tracking-wider mb-1">{data.tipo_solicitud === 'tramite' ? 'Trámite' : 'Motivo'}</dt><dd className="text-slate-800 font-medium">{data.tramite_label}</dd></div>
              {data.tar && <div><dt className="text-xs text-slate-500 uppercase tracking-wider mb-1">TAR</dt><dd className="text-slate-800 font-medium">{data.tar}</dd></div>}
              {data.matricula && <div><dt className="text-xs text-slate-500 uppercase tracking-wider mb-1">Matrícula</dt><dd className="text-slate-800 font-medium flex items-center gap-1"><Plane className="h-4 w-4 text-violet-500" /> {data.matricula}</dd></div>}
              <div><dt className="text-xs text-slate-500 uppercase tracking-wider mb-1">Fecha de envío</dt><dd className="text-slate-800 font-medium flex items-center gap-1"><Calendar className="h-4 w-4 text-violet-500" /> {formatDate(data.created_at)}</dd></div>
              {data.cantidad_adjuntos > 0 && <div><dt className="text-xs text-slate-500 uppercase tracking-wider mb-1">Adjuntos iniciales</dt><dd className="text-slate-800 font-medium flex items-center gap-1"><Paperclip className="h-4 w-4 text-violet-500" /> {data.cantidad_adjuntos} archivo(s)</dd></div>}
            </dl>
          </CardContent>
        </Card>

        {/* Contacto */}
        <Card className="bg-violet-50 border-violet-200">
          <CardContent className="p-6 text-center">
            <p className="text-slate-700 mb-3">¿Dudas sobre tu solicitud? Contactanos directamente:</p>
            <div className="flex flex-col sm:flex-row justify-center gap-2">
              <Button asChild variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-100">
                <a href="mailto:administracion@cmpublicaciones.com">administracion@cmpublicaciones.com</a>
              </Button>
              <Button asChild variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-100">
                <a href="tel:+543517327514">+54 351 732-7514</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GestoriaSeguimientoPage;
